// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ConfigHandler from '../../loaders/handlers/ConfigHandler';
import AnimationClip from '../../animationpack/clip/AnimationClip';
import JointChannel from '../../animationpack/clip/JointChannel';
import TransformChannel from '../../animationpack/clip/TransformChannel';
import InterpolatedFloatChannel from '../../animationpack/clip/InterpolatedFloatChannel';
import TriggerChannel from '../../animationpack/clip/TriggerChannel';
import ArrayUtils from '../../util/ArrayUtils';
import UndefinedBinaryDataError from 'core/errors/UndefinedBinaryDataError';

/**
 * Handler for loading animation clips into engine
 */
class AnimationClipHandler extends ConfigHandler {

  /**
   * @param {...*} args {@link ConfigHandler} constructor arguments.
   */
  constructor(...args) {
    super(...args);
  }

  /**
   * Creates an empty animation clip
   * @returns {AnimationClip}
   * @private
   */
  _create() {
    return new AnimationClip();
  }

  /**
   * Adds/updates/removes an animation clip
   * @param {string} ref
   * @param {Object} config
   * @param {Object} options
   * @returns {Promise} Resolves with the updated animation clip or null if removed
   */
  _update(ref, config, options) {
    const that = this;
    return ConfigHandler.prototype._update.call(this, ref, config, options).then((clip) => {
      if (!clip) { return clip; }
      return that.loadObject(config.binaryRef, options).then((bindata) => {
        if (bindata == null) {
          throw new UndefinedBinaryDataError({type: 'Binary clip', data: bindata, ref: config.binaryRef});
        }
        return that._updateAnimationClip(config, bindata, clip);
      });
    });
  }

  /**
   * Does the actual updating of animation clip and channels from clipConfig
   * It creates new channels on every update, but clips are practically never updated
   * @param {Object} clipConfig config with the clip data to be updated
   * @param {ArrayBuffer} binData data array with channel data
   * @param {AnimationClip} clip the destination clip to receive the update
   * @private
   */
  _updateAnimationClip(clipConfig, binData, clip) {
    clip._channels = [];

    if (clipConfig.channels) {
      const keys = Object.keys(clipConfig.channels);
      for (let i = 0; i < keys.length; i++) {
        const channelConfig = clipConfig.channels[keys[i]];
        // Time samples
        const times = ArrayUtils.getTypedArray(binData, channelConfig.times);

        const blendType = channelConfig.blendType;
        const type = channelConfig.type;

        var channel;
        switch (type) {
          case 'Joint':
          case 'Transform':
            // Transform samples
            var rots,
              trans,
              scales;
            rots = ArrayUtils.getTypedArray(binData, channelConfig.rotationSamples);
            trans = ArrayUtils.getTypedArray(binData, channelConfig.translationSamples);
            scales = ArrayUtils.getTypedArray(binData, channelConfig.scaleSamples);

            if (type === 'Joint') {
              channel = new JointChannel(
                channelConfig.name,
                times,
                rots,
                trans,
                scales,
                blendType,
                channelConfig.additiveBaseRotation,
                channelConfig.additiveBaseTranslation,
                channelConfig.additiveBaseScale
              );
            } else {
              channel = new TransformChannel(
                channelConfig.name,
                times,
                rots,
                trans,
                scales,
                blendType,
              );
            }
            break;
          case 'FloatLERP':
            channel = new InterpolatedFloatChannel(
              channelConfig.name,
              times,
              channelConfig.values,
              blendType,
            );
            break;
          case 'Trigger':
            channel = new TriggerChannel(
              channelConfig.name,
              times,
              channelConfig.keys,
            );
            channel.guarantee = !!channelConfig.guarantee;
            break;
          default:
            console.warn(`[Sumerian] Unhandled channel type: ${channelConfig.type}`);
            continue;
        }
        clip.addChannel(channel);
      }
    }

    clip.isAdditive = clipConfig.isAdditive;  // Note: this is undefined for clips save prior to Nov 2018
    clip.name = clipConfig.name;

    return clip;
  }
}

ConfigHandler._registerClass('clip', AnimationClipHandler);

export default AnimationClipHandler;
